/*
 * @Author: Ryan
 * @Date: 2026-03-11
 * @Description: Testimonials section - What Our Customers Say
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Finally, I Can Scale!',
    content:
      "I used to spend my entire weekend filming content, only to end up with three usable videos. With FalcoCut, I just input my scripts and my AI twin handles the rest while I focus on running my shop. It's a total lifework-balance game changer.",
    role: 'E-commerce Store Owner',
    name: 'Sarah Stratvert',
  },
  {
    title: 'Goodbye, Expensive Studios',
    content:
      "Hiring a production crew for every product launch was eating all our margins. Now, we generate high-converting ads in minutes using our digital twins for a fraction of the cost. The realism is so good that our customers can't even tell the difference.",
    role: 'Marketing Director',
    name: 'Dan Thorne',
  },
  {
    title: 'Content Creation on Autopilot',
    content:
      "I'm pretty camera-shy, which made growing my personal brand feel impossible. This tool let me create a professional AI version of myself that delivers perfect takes every single time. My engagement has doubled since I started posting daily.",
    role: 'Digital Content Creator',
    name: 'Chloe Richardson',
  },
  {
    title: 'No More Re-shoots',
    content:
      'Nothing was more frustrating than finding a mistake in a video after the set was already torn down. With this AI twin generator, I just tweak the text and hit regenerate to fix any error instantly. It has saved us so much time and technical headache.',
    role: 'Social Media Manager',
    name: 'David Preece',
  },
  {
    title: 'Global Reach Made Easy',
    content:
      "Trying to localize our ads for international markets used to be a nightmare of finding voice actors. Now, our AI twin speaks over 30 languages fluently, allowing us to test new markets in a single afternoon. It's the fastest way to take a brand global.",
    role: 'Brand Strategist',
    name: 'Elena James',
  },
  {
    title: 'The Ultimate Efficiency Hack',
    content:
      "I was skeptical about AI video, but the quality of these twins blew me away. We've replaced our entire weekly filming schedule with a few hours of scriptwriting and automated rendering. Our output has increased by 10x without adding any extra staff.",
    role: 'Creative Lead',
    name: 'Mitch Wilson',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg key={star} width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
